/** Shared presentational pieces. Deliberately plain — no component library. */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { CatalogItemDto } from '@filmodasi/shared';

export function Spinner({ label = 'Yükleniyor…' }: { label?: string }) {
  return (
    <div className="spinner" role="status" aria-live="polite">
      <span className="spinner__dot" />
      {label}
    </div>
  );
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="error" role="alert">
      <p>{message}</p>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          Tekrar dene
        </button>
      )}
    </div>
  );
}

/**
 * Nothing here — and, where possible, what to do about it.
 *
 * An empty state is not an error and should not be dressed as one. It is usually the very
 * first thing a new household sees on Home and on all three libraries, so a single grey
 * sentence in the middle of a black page is the app's opening impression.
 *
 * The three parts are separate because they answer different questions. The `title` says
 * what is empty, the body says why, and the `action` is the one thing worth doing about
 * it. All but the body are optional: "Geçmiş boş." needs no heading and no button, and
 * inventing one for it would be worse than the plain sentence.
 *
 * `role="status"` so a filter that comes back with nothing is announced rather than
 * leaving a screen reader on a page that silently stopped having results.
 */
export function Empty({
  title,
  children,
  action,
}: {
  title?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty" role="status">
      {title && <strong className="empty__title">{title}</strong>}
      <p className="empty__body">{children}</p>
      {action && <div className="empty__action">{action}</div>}
    </div>
  );
}

/**
 * A grid of placeholder tiles, shown while the first page loads.
 *
 * Preferred over the spinner wherever the shape of what is coming is known. A spinner says
 * "something is happening somewhere"; this says "a grid of posters is arriving here", and
 * the page does not jump when it does — which is the actual complaint behind most
 * "the app feels slow" impressions on a connection that is merely ordinary.
 *
 * `aria-hidden`, with the live region left to the caller: a screen reader announcing
 * twelve empty tiles is worse than silence.
 */
export function SkeletonGrid({ count = 12, poster = true }: { count?: number; poster?: boolean }) {
  return (
    <div className="grid" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={`skeleton ${poster ? 'card--poster' : 'card--wide'}`}>
          <div className="skeleton__art" />
          <div className="skeleton__line" />
          <div className="skeleton__line skeleton__line--short" />
        </div>
      ))}
    </div>
  );
}

/**
 * The homepage's shape, before the homepage has arrived.
 *
 * `SkeletonGrid` is the wrong placeholder for Home: Home is not a grid, and swapping a
 * twelve-tile block for three horizontal rails moves everything on the page the moment the
 * data lands. This mirrors what Home actually renders — a title, then a rail — so the
 * transition is a fade from grey to artwork rather than a relayout.
 *
 * Three rails and six cards is a deliberate under-estimate. Home usually returns more of
 * both, and drawing *fewer* placeholders than will arrive means the page grows downward
 * into empty space; drawing more means it collapses upward, which pulls whatever the reader
 * was already looking at out from under them.
 *
 * `aria-hidden` for the same reason as `SkeletonGrid`. Callers pair it with `.sr-only`.
 */
export function SkeletonRails({
  rows = 3,
  cards = 6,
  poster = true,
}: {
  rows?: number;
  cards?: number;
  /** False for a live-TV rail: wide logo tiles are both a different ratio and wider. */
  poster?: boolean;
}) {
  return (
    <div aria-hidden>
      {Array.from({ length: rows }, (_, r) => (
        <section className="row" key={r}>
          <div className="row__head">
            <div className="skeleton__line skeleton__line--title" />
          </div>
          <div className="row__rail">
            {Array.from({ length: cards }, (_, c) => (
              // `card card--poster|--wide` so it takes the same responsive width *and* the
              // same ratio as the card that replaces it. `.row__rail > .card` sets the
              // width and the two modifiers set the ratio; getting either wrong reintroduces
              // exactly the jump the skeleton exists to prevent.
              <div key={c} className={`skeleton card card--${poster ? 'poster' : 'wide'}`}>
                <div className="skeleton__art" />
                <div className="skeleton__line" />
                <div className="skeleton__line skeleton__line--short" />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * A catalog tile.
 *
 * Live channels get a wide logo tile, VOD gets a portrait poster — the two libraries
 * have genuinely different artwork shapes and forcing one aspect ratio makes both look
 * broken.
 */
import { PlayOnTvButton } from './PlayOnTvButton';
import { MediaImage } from './MediaImage';
import { maturityLabel } from '../catalog/maturity';
import { detailPath } from '../catalog/detailPath';

export function ItemCard({ item }: { item: CatalogItemDto }) {
  const isLive = item.type === 'live';
  const art = isLive ? item.logo : (item.poster ?? item.logo);
  const maturity = maturityLabel(item.maturity);

  return (
    <Link
      /*
        Through `detailPath`, which the television already used.

        The two layouts disagreed about what a card does: selecting one on the panel opened
        a detail screen, clicking the same card on a laptop started the stream. That was not
        a design choice, it was the web arm having no detail screen to go to — and it meant
        the one device where a connection slot is easy to burn by accident was the one that
        started playback on a single click. One rule now, in one place.
      */
      to={detailPath(item)}
      className={`card ${isLive ? 'card--wide' : 'card--poster'}`}
      title={item.title}
    >
      <div className="card__art">
        <MediaImage
          src={art}
          shape={isLive ? 'wide' : 'poster'}
          fallback={item.title}
          // The id, not the title: a retitled item keeps its colour, and two films sharing
          // a name do not share one.
          seed={item.id}
        />
        {/*
          The age band, top corner.

          Only `teen` and `adult` produce one — see `maturityLabel`. It is a caution on a
          shared television, not a rating system, and it is drawn rather than blurred:
          §17's position is that filtered content is invisible, because a blurred tile
          advertises exactly what it is hiding. What reaches this card is content the
          profile is allowed to see; the badge only says what kind.
        */}
        {maturity && <span className="card__age">{maturity}</span>}
        {/* A caution, not a verdict — Chrome plays .mkv, Firefox does not. */}
        {!item.playable && item.containerExt && (
          <span className="card__badge">.{item.containerExt}</span>
        )}
        {/*
          "Play on TV", on the card rather than on the player.

          This is the whole of requirement 6: browse on the phone, watch on the television.
          Putting it on the watch page instead would mean the phone had already opened a
          stream — burning one of the subscription's connection slots — before offering to
          move it somewhere else.

          It renders nothing at all when no television is on the channel, so it costs the
          grid no space in the ordinary case. Series go to their own screen instead: a
          series has no stream to start, and the choice of episode is the point.
        */}
        {item.type !== 'series' && <PlayOnTvButton itemId={item.id} onCard />}
      </div>
      <span className="card__title">{item.title}</span>
      {item.year && <span className="card__meta">{item.year}</span>}
    </Link>
  );
}

export function ItemGrid({
  items,
  hasMore,
  loading,
  onLoadMore,
}: {
  items: CatalogItemDto[];
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
}) {
  return (
    <>
      <div className="grid">
        {items.map((item) => (
          <ItemCard key={item.id} item={item} />
        ))}
      </div>
      {loading && <Spinner />}
      {hasMore && !loading && (
        <button type="button" className="load-more" onClick={onLoadMore}>
          Daha fazla göster
        </button>
      )}
    </>
  );
}
